import { useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import type { ProjectJob } from '../../api/types';
import { IndexerChartCard } from '../IndexerChartCard';
import { indexOutcomeBarSeries } from '../../lib/chart-colors';
import {
  aggregateIndexJobsByDay,
  indexUrlOutcomeSlices,
  jobRequestOutcomeSlices,
} from '../../lib/overview-stats';
import { statsRangeHint, type StatsRange } from '../../lib/job-range';
import { DonutChart } from './DonutChart';
import { StackedBarChart } from './StackedBarChart';

type OutcomeView = 'requests' | 'urls';

const OUTCOME_VIEW_OPTIONS: {
  value: OutcomeView;
  label: string;
  hint: string;
}[] = [
  {
    value: 'requests',
    label: 'نتیجهٔ درخواست‌ها',
    hint: 'تعداد جاب در بازه',
  },
  {
    value: 'urls',
    label: 'نتیجهٔ آدرس‌ها',
    hint: 'جمع URL در همهٔ جاب‌ها',
  },
];

export function IndexPageCharts({
  jobs,
  rangeDays,
}: {
  jobs: ProjectJob[];
  rangeDays: StatsRange;
}) {
  const [outcomeView, setOutcomeView] = useState<OutcomeView>('requests');
  const rangeHint = statsRangeHint(rangeDays);

  const daily = useMemo(
    () => aggregateIndexJobsByDay(jobs, rangeDays),
    [jobs, rangeDays],
  );
  const requestSlices = useMemo(
    () => jobRequestOutcomeSlices(jobs),
    [jobs],
  );
  const urlSlices = useMemo(() => indexUrlOutcomeSlices(jobs), [jobs]);

  const outcomeOption =
    OUTCOME_VIEW_OPTIONS.find((item) => item.value === outcomeView) ??
    OUTCOME_VIEW_OPTIONS[0]!;
  const outcomeSlices = outcomeView === 'requests' ? requestSlices : urlSlices;

  return (
    <div className="row g-3">
      <div className="col-lg-6">
        <IndexerChartCard
          className="overview-chart-card h-100 mb-0"
          title="درخواست ایندکس"
          subtitle={`آدرس‌های موفق و ناموفق در بازهٔ ${rangeHint}`}
          guideKey="index-daily-activity"
        >
          <StackedBarChart data={daily} series={indexOutcomeBarSeries()} />
        </IndexerChartCard>
      </div>
      <div className="col-lg-6">
        <IndexerChartCard
          className="overview-chart-card h-100 mb-0"
          title={outcomeOption.label}
          subtitle={outcomeOption.hint}
          guideKey={
            outcomeView === 'requests'
              ? 'index-outcome-requests'
              : 'index-outcome-urls'
          }
          endEl={
            <Form.Select
              size="sm"
              className="w-auto"
              value={outcomeView}
              onChange={(e) => setOutcomeView(e.target.value as OutcomeView)}
              aria-label="نوع نمودار نتیجه"
            >
              {OUTCOME_VIEW_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </Form.Select>
          }
        >
          <DonutChart key={outcomeView} slices={outcomeSlices} height={260} />
        </IndexerChartCard>
      </div>
    </div>
  );
}
